// TransactionSuccessModal.jsx
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStethoscope,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import GenderDisplay from "./getGenderDisplay";
import ActionButtons from "./ActionButtons";

const formatProfessionalFee = (value) => {
  if (value === null || value === undefined) return "N/A";

  const normalizedValue = String(value).trim();
  if (
    normalizedValue === "" ||
    normalizedValue === "-" ||
    normalizedValue.toUpperCase() === "N/A"
  ) {
    return "N/A";
  }

  const amount = Number(normalizedValue.replace(/,/g, ""));
  if (!Number.isFinite(amount)) return normalizedValue;

  return `\u20b1 ${amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const isMissingValue = (value) =>
  value === null ||
  value === undefined ||
  String(value).trim() === "" ||
  String(value).trim().toUpperCase() === "N/A";

const displayValue = (value) => (isMissingValue(value) ? "N/A" : String(value));

const getFinalDiagnosis = (data) => {
  if (!isMissingValue(data?.final_diagnosis)) return data.final_diagnosis;
  if (!isMissingValue(data?.finalDiagnosis)) return data.finalDiagnosis;

  const icdCode = !isMissingValue(data?.icd_10_cm)
    ? `(${String(data.icd_10_cm).trim()})`
    : "";
  const condition = !isMissingValue(data?.condition)
    ? String(data.condition).trim()
    : "";
  const formattedDiagnosis = [icdCode, condition].filter(Boolean).join(" -- ");

  if (formattedDiagnosis) return formattedDiagnosis;
  return data?.diagnosis;
};

const getLoaLabProcedures = (data) => {
  const candidates = [
    data?.loa_lab_procedures,
    data?.loaLabProcedures,
    data?.loa_cpt?.rows,
    data?.loa_cpt,
    data?.loaCpt?.rows,
    data?.loaCpt,
    data?.procedures,
  ];

  return (
    candidates.find((value) => Array.isArray(value) && value.length > 0) || []
  );
};

const formatLoaLabProcedure = (item, index) => {
  if (typeof item === "string") {
    return { title: item, meta: [] };
  }

  const cpt = item?.cpt || {};
  const title =
    item?.cpt_desc ||
    cpt?.cpt_desc ||
    item?.description ||
    item?.procedure_name ||
    item?.name ||
    item?.cpt_code ||
    cpt?.cpt_code ||
    item?.loa_cpt_code ||
    item?.provider_cpt_code ||
    item?.cpt_pkg_code ||
    `Procedure ${index + 1}`;
  const rate =
    item?.cpt_rate ??
    cpt?.cpt_rate ??
    item?.rate ??
    item?.loa_cpt_amount ??
    item?.loa_cpt_amt ??
    item?.amount ??
    item?.default_price;
  const meta = [
    item?.cpt_code || cpt?.cpt_code
      ? `CPT: ${item?.cpt_code || cpt?.cpt_code}`
      : "",
    item?.provider_cpt_code || cpt?.provider_cpt_code
      ? `Provider CPT: ${item?.provider_cpt_code || cpt?.provider_cpt_code}`
      : "",
    item?.cpt_pkg_code ? `Package: ${item.cpt_pkg_code}` : "",
    !isMissingValue(rate) ? `Rate: ${formatProfessionalFee(rate)}` : "",
  ].filter(Boolean);

  return { title, meta };
};

const TransactionSuccessModal = ({
  isOpen,
  onClose,
  selectedMember,
  selectedProvider,
  selectedCoverage,
  selectedTransNo,
  selectedLoaNo,
  transactionData,
  onViewMemberInfo,
  onContinue,
  onCancel,
  flowType,
  continueDisabled = false,
  cancelDisabled = false,
  disabledReason = "",
}) => {
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [shouldBump, setShouldBump] = useState(false);
  const modalRef = useRef(null);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        // Instead of closing, trigger bump animation
        setShouldBump(true);
        setTimeout(() => setShouldBump(false), 300); // Reset after animation
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle Escape key to close
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const finalDiagnosis = getFinalDiagnosis(transactionData);
  const loaLabProcedures = getLoaLabProcedures(transactionData);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
        // Remove the onClick from here to prevent closing on backdrop click
      >
        <motion.div
          ref={modalRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ 
            scale: shouldBump ? [1, 1.02, 1] : 1,
            opacity: 1 
          }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{
            duration: shouldBump ? 0.3 : 0.2,
            ease: shouldBump ? "easeInOut" : "easeOut"
          }}
          // Remove the onClick stopPropagation since we're handling outside clicks differently
          className="bg-white rounded-2xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden shadow-2xl border border-gray-100 flex flex-col"
        >
          <div className="flex shrink-0 items-center justify-between mb-4">
            <h2 className="text-2xl text-gray-900 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faStethoscope}
                className="text-green-600"
              />
              -Transaction Details - {selectedCoverage?.text || "Consultation"}
            </h2>
            <button
              // onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FontAwesomeIcon icon={faStethoscope} className="text-xl" />
            </button>
          </div>
          
          <div className="min-h-0 space-y-4 overflow-y-auto pr-2">
            {/* Basic Info Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Member Name
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {selectedMember?.text || "N/A"}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Transaction #
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {selectedTransNo}
                </p>
              </div>
              {selectedLoaNo && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    LOA #
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedLoaNo}
                  </p>
                </div>
              )}
            </div>

            {/* Other Details Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Company
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {transactionData?.corp_name || "N/A"}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Plan Expiry
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {transactionData?.plan_expiry || "N/A"}
                </p>
              </div>
            </div>

            {/* Provider, Gender, Age Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Provider Name
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {selectedProvider?.text || "N/A"}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Gender
                </label>
                <GenderDisplay gender={transactionData?.gender} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Age</label>
                <p className="text-lg font-semibold text-gray-900">
                  {transactionData?.age || "N/A"}
                </p>
              </div>
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Date Issued
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {transactionData?.date_issued || "N/A"}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Valid Until
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {transactionData?.valid_until || "N/A"}
                </p>
              </div>
            </div>

            {/* Physician and Fee Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Physician Name
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {transactionData?.physician_name || "N/A"}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Professional Fee/PCP Rate
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatProfessionalFee(transactionData?.prof_fee)}
                </p>
              </div>
            </div>

            {/* Remarks */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="text-sm font-medium text-gray-500 block mb-2">
                Remarks
              </label>
              <p className="text-gray-700">
                {transactionData?.remarks || "null"}
              </p>
            </div>

            {/* Accordion for Chief Complaint */}
            <motion.div
              initial={false}
              className="bg-white border border-gray-200 rounded-lg"
            >
              <motion.div
                whileHover={{ backgroundColor: "#f3f4f6" }}
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => setAccordionOpen(!accordionOpen)}
              >
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FontAwesomeIcon icon={faStethoscope} />
                  Chief Complaint & Requested Lab Procedure
                </h4>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`text-gray-400 transition-transform ${
                    accordionOpen ? "rotate-180" : ""
                  }`}
                />
              </motion.div>
              <AnimatePresence>
                {accordionOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Chief Complaint
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Chief Complaint"
                          readOnly
                          value={displayValue(transactionData?.chief_complaint)}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Requested Lab Procedure
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Requested Lab Procedure"
                          readOnly
                          value={displayValue(
                            transactionData?.requested_lab ||
                              transactionData?.requested_lab_procedure
                          )}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Final Diagnosis
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border border-gray-300 rounded-md"
                          placeholder="Final Diagnosis"
                          readOnly
                          value={displayValue(finalDiagnosis)}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          LOA Lab Procedure(s)
                        </label>
                        {loaLabProcedures.length === 0 ? (
                          <div className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-900">
                            N/A
                          </div>
                        ) : (
                          <ul className="mt-1 space-y-2">
                            {loaLabProcedures.map((item, index) => {
                              const procedure = formatLoaLabProcedure(item, index);

                              return (
                                <li
                                  key={`${procedure.title}-${index}`}
                                  className="rounded-md border border-gray-300 px-3 py-2"
                                >
                                  <p className="text-sm font-semibold text-gray-900">
                                    {displayValue(procedure.title)}
                                  </p>
                                  {procedure.meta.length > 0 && (
                                    <p className="mt-1 text-xs text-gray-500">
                                      {procedure.meta.join(" | ")}
                                    </p>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Sticky Action Footer */}
          <div className="sticky bottom-0 z-10 shrink-0 bg-white/95 backdrop-blur-sm">
            {(continueDisabled || cancelDisabled) && disabledReason && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                {disabledReason}
              </div>
            )}
            <ActionButtons
              onViewMemberInfo={onViewMemberInfo}
              onContinue={onContinue}
              onCancel={onCancel}
              onClose={onClose}
              flowType={flowType}
              continueDisabled={continueDisabled}
              cancelDisabled={cancelDisabled}
              disabledReason={disabledReason}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TransactionSuccessModal;
